import React, { useCallback, useMemo, useRef, useState } from 'react';

import calendarSvg from '@assets/img/icons/calendar.svg';
import infoGraySvg from '@assets/img/icons/info-gray.svg';
import infoSvg from '@assets/img/icons/info.svg';
import { ExpandMoreButton } from '@components/ExpandMoreButton';
import { useBookingCreateContext } from '@components/utilities/BookingInquiry/BookingCreateContext';
import { PriceSettlement2 } from '@components/utilities/BookingInquiry/PriceSettlement2';
import { RoutePlanLineDisplay } from '@components/utilities/BookingInquiry/RoutePlanLineDisplay';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { receivingWarehouseChineseHolidays } from '@data/bookingInquiryHolidays';
import { moneyStrings } from '@data/money';
import { routePlanServiceNameStrings, routePlanStrings } from '@data/routePlan';
import Collapse from '@material-ui/core/Collapse';
import { Cargo2, CargoSummary, getSummaryOfCargo2 } from '@models/BookingInquiry';
import { BookingInquiryHelper } from '@models/next/BookingInquiryHelper';
import {
  getSimulatorResultTotalPrice,
  routePlanOrder,
  type RoutePlanOrder,
  type RoutePlanSimulator,
  type ServiceName,
  type SimulatorResult,
} from '@models/RoutePlan';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { joinKeyPath } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';
import { useField } from 'formik';
import { minBy, sortBy, sum } from 'lodash-es';
import useHoverDirty from 'react-use/lib/useHoverDirty';

const strings = createStrings({
  ja: {
    select: '選択する',
    receivingWarehouseInHoliday: '荷受倉庫休業中',
    noRoutesAvailable: `該当する${routePlanStrings.route}はありませんでした。 集荷日や荷受倉庫への搬入予定日など予約内容を変更してください。`,
    noFilteredRoutesAvailable: `フィルターに該当する${routePlanStrings.route}はありません。`,
    daysHint: () => (
      <>
        集荷希望日もしくは荷受倉庫への搬入予定日から納品までの所要日数です。
        <br />
        輸送状況により変動する可能性がございます。
      </>
    ),
    bestDeliveryDateHint: () => (
      <>
        表示の日時は現時点での概算によるものです。中国および日本の祝祭日や、天候・ストライキなどの影響により最短納品可能日が前後する可能性もございます。
        <br />
        ご了承ください。
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  routePlanListContainer: {
    ...common.flexColumn,
    backgroundColor: 'transparent',
    flex: 1,
    rowGap: '1rem',
    maxHeight: '100%',
  },
  routePlanOrderOptionsContainer: {
    ...common.flexInline,
    ...common.smallerText,
    ...common.textPrimary,
    ...common.fontWeight600,
  },
  routePlanOrderOption: {
    ...common.textAlignCenter,
    ...common.baseButton,
    paddingTop: 2,
    flex: 1,
    height: 24,
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.common.white,
    '&:first-child': {
      borderTopLeftRadius: 12,
      borderBottomLeftRadius: 12,
    },
    '&:last-child': {
      borderTopRightRadius: 12,
      borderBottomRightRadius: 12,
    },
    '&$selected': {
      borderColor: theme.palette.primary.main,
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
  },
  routePlanCardContainer: {
    ...common.flexColumn,
    ...common.baseButton,
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 0px 17px 4px rgba(220, 222, 224, 0.4)',
    '&:hover': {
      border: `2px solid ${theme.palette.primary.main}`,
      '$disabled&': {
        border: `2px solid ${theme.palette.grey[700]}`,
      },
    },
    border: `2px solid transparent`,
  },
  routePlanCardContainerTop: {
    ...common.flexInline,
    padding: '20px 41px',
  },
  routePlanCardContainerLeft: {
    ...common.flexColumn,
    flex: 1,
  },
  warehouseDisplayContainer: {
    ...common.flexInline,
    ...common.textGray700,
    ...common.exSmallerText,
    marginTop: 10,
  },
  warehouseDisplayLeft: {
    ...common.flexColumn,
    ...common.itemsCenter,
    width: 20,
  },
  warehouseDisplayRight: {
    flex: 1,
  },
  routePlanCardDividerContainer: {
    ...common.flexInline,
    ...common.justifyCenter,
    width: 30,
    padding: '0 25px',
  },
  routePlanCardDivider: {
    width: 1,
    backgroundColor: theme.palette.grey[300],
  },
  priceDisplayContainer: {
    ...common.flexColumnCenter,
  },
  priceDisplay: {
    ...common.normalText,
    ...common.fontWeight600,
    ...common.textGray800,
    fontSize: 26,
    marginBottom: 12,
  },
  priceDisplaySelectButton: {
    ...common.flexColumnCenter,
    ...common.smallerText,
    ...common.textPrimary,
    ...common.fontWeight600,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 4,
    width: 136,
    height: 36,
    marginBottom: 8,
    '&:hover': {
      border: `1px solid ${theme.palette.primary.dark}`,
      color: theme.palette.primary.dark,
    },
    '$disabled &': {
      ...common.textWhite,
      border: `1px solid ${theme.palette.grey[500]} !important`,
      backgroundColor: theme.palette.grey[500],
      cursor: 'not-allowed',
    },
  },
  hintContainer: {
    ...common.exSmallerText,
    ...common.textGray700,
    padding: 9,
    backgroundColor: '#F2F7F8',
    position: 'absolute',
    width: 222,
    zIndex: 100,
    top: 0,
    left: '200%',
    border: `1px solid ${theme.palette.primary.main}`,
  },
  bestDeliveryDateHintContainer: {
    ...common.exSmallerText,
    ...common.textGray700,
    padding: 9,
    backgroundColor: '#F2F7F8',
    position: 'absolute',
    width: 222,
    zIndex: 100,
    top: '140%',
    right: 0,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  routePlanCardExpandArea: {
    ...common.flexCenter,
    ...common.textPrimary,
    marginTop: '5px',
    marginBottom: '5px',
    marginLeft: '24px',
    marginRight: '24px',
  },
  routePlanCardTags: {
    ...common.flexInline,
    columnGap: 16,
  },
  routePlanCardTag: {
    ...common.flexCenter,
    ...common.textPrimary,
    ...common.exSmallerText,
    ...common.selfStart,
    ...common.fontWeight600,
    height: 20,
    padding: '0 14px',
    borderRadius: 2,
    '$disabled &': {
      ...common.textWhite,
    },
  },
  split: {
    backgroundColor: theme.palette.grey[300],
    height: 1,
    flex: 1,
  },
  tagServiceName: {
    backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    '$disabled &': {
      backgroundColor: Color(theme.palette.grey[700]).alpha(0.3).toString(),
    },
  },
  tagHasPickups: {
    backgroundColor: Color(theme.palette.accent.main).alpha(0.1).toString(),
    '$disabled &': {
      backgroundColor: Color(theme.palette.grey[700]).alpha(0.3).toString(),
    },
  },
  selected: {},
  disabled: {},
}));

const RoutePlanOrderOptions = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [field, , helpers] = useField<RoutePlanOrder>('route_plan_order');

  return (
    <div className={classes.routePlanOrderOptionsContainer}>
      {routePlanOrder.types.map((literal) => (
        <div
          key={literal.value}
          className={clsx(
            classes.routePlanOrderOption,
            field.value === literal.value && classes.selected,
          )}
          onClick={() => helpers.setValue(literal.value)}
        >
          {routePlanStrings[literal.value]}
        </div>
      ))}
    </div>
  );
};

const RoutePlanCard: React.FC<{
  item: RoutePlanSimulator;
  cargoSummary: CargoSummary;
  isHoliday: boolean;
}> = React.memo(({ item, cargoSummary, isHoliday }) => {
  const { isDashboard, resolvePostBookingInquiryResult, ownerCompanyCountry } =
    useBookingCreateContext();

  const [, , routePlanHelpers] = useField<number>('route_plan');
  const [, , cargosPortNameHelpers] = useField<string>('cargos_port_name');
  const [, , simulatorResultPlanHelpers] = useField<SimulatorResult>('route_plan_simulator_result');

  const resolvedPostBookingInquiry = resolvePostBookingInquiryResult?.postBookingInquiry!;

  const hasPickups = !!resolvedPostBookingInquiry.cargos.cargos_by_pickup_address;

  const [expanded, setExpanded] = useState(false);

  const daysInfoRef = useRef<HTMLImageElement>(null);

  const isHoveringDaysInfo = useHoverDirty(daysInfoRef);

  const bestDeliveryDateInfoRef = useRef<HTMLImageElement>(null);

  const isHoveringBestDeliveryDate = useHoverDirty(bestDeliveryDateInfoRef);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderRoutePlanTitle = () => {
    return (
      <div className={clsx(classes.middleText, classes.fontWeight600, classes.textGray700)}>
        {item.route_plan.name}
        {' | '}
        <span
          className={clsx(isHoliday ? classes.textGray700Important : classes.textGray900Important)}
        >
          {item.simulator.delivery_days} day{item.simulator.delivery_days > 1 ? 's' : ''}
        </span>

        <span style={{ position: 'relative' }}>
          <img
            src={isHoliday ? infoGraySvg : infoSvg}
            alt="Info"
            ref={daysInfoRef}
            style={{ left: 6, position: 'relative', cursor: 'help', top: 2 }}
          />
          {isHoveringDaysInfo && <div className={classes.hintContainer}>{strings.daysHint()}</div>}
        </span>
      </div>
    );
  };

  const renderPriceDisplay = () => {
    const bestDeliveryDate = dayjs(
      hasPickups
        ? resolvedPostBookingInquiry.pickup_date
        : resolvedPostBookingInquiry?.move_in_date,
    ).add(item.simulator.delivery_days, 'day');

    return (
      <div className={classes.priceDisplayContainer}>
        <div
          className={clsx(classes.smallerText, classes.textGray500, classes.fontWeight600)}
          style={{ paddingBottom: '0.25rem' }}
        >
          {ownerCompanyCountry === 'jp'
            ? bookingInquiryStrings.consigneeSide
            : bookingInquiryStrings.shipperSide}
        </div>
        <div className={classes.priceDisplay}>
          {moneyStrings.formatYen(
            getSimulatorResultTotalPrice(item.simulator, ownerCompanyCountry),
          )}
        </div>

        <button
          className={classes.priceDisplaySelectButton}
          type="submit"
          disabled={isHoliday}
          onClick={(e) => {
            e.stopPropagation();
            simulatorResultPlanHelpers.setValue({
              ...item.simulator,
              ...resolvePostBookingInquiryResult?.simulatorResultCities,
            });
            cargosPortNameHelpers.setValue(item.route_plan.receiving_city);
            routePlanHelpers.setValue(item.route_plan.id);
          }}
        >
          {isHoliday ? strings.receivingWarehouseInHoliday : strings.select}
        </button>

        <div
          className={clsx(
            classes.exSmallerText,
            classes.textGray500Important,
            classes.fontWeight600Important,
          )}
        >
          <img
            src={calendarSvg}
            alt="Calendar"
            style={{ marginRight: 2, position: 'relative', top: 1 }}
          />

          {bookingInquiryStrings.bestDeliveryDate}
          {': '}
          {bestDeliveryDate.format('YYYY/MM/DD')}
          <span style={{ position: 'relative' }}>
            <img
              src={isHoliday ? infoGraySvg : infoSvg}
              alt="Info"
              ref={bestDeliveryDateInfoRef}
              style={{
                left: 6,
                position: 'relative',
                cursor: 'help',
                top: 2,
                height: 11,
                width: 11,
              }}
            />
            {isHoveringBestDeliveryDate && (
              <div className={classes.bestDeliveryDateHintContainer}>
                {strings.bestDeliveryDateHint()}
              </div>
            )}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="RoutePlanCard"
      className={clsx(classes.routePlanCardContainer, isHoliday && classes.disabled)}
      onClick={() => setExpanded(!expanded)}
    >
      <div className={classes.routePlanCardContainerTop}>
        <div className={classes.routePlanCardContainerLeft}>
          <div className={classes.routePlanCardTags}>
            <div className={clsx(classes.routePlanCardTag, classes.tagServiceName)}>
              {routePlanServiceNameStrings[item.route_plan.service_name]}
            </div>

            <div className={clsx(classes.routePlanCardTag, classes.tagHasPickups)}>
              {hasPickups ? bookingInquiryStrings.needsPickup : bookingInquiryStrings.noPickup}
            </div>
          </div>
          <div style={{ height: 8 }} />
          {renderRoutePlanTitle()}
          <div style={{ height: 8 }} />

          <RoutePlanLineDisplay
            routePlan={BookingInquiryHelper.mapToRoutePlan(
              {
                ...item,
                simulator: {
                  ...item.simulator,
                  ...resolvePostBookingInquiryResult?.simulatorResultCities,
                },
              },
              resolvedPostBookingInquiry,
            )}
            hideIcons={isDashboard}
            disabled={isHoliday}
          />
        </div>

        <div className={classes.routePlanCardDividerContainer}>
          <div className={classes.routePlanCardDivider} />
        </div>

        {renderPriceDisplay()}
      </div>

      <div className={classes.routePlanCardExpandArea}>
        <ExpandMoreButton
          expanded={expanded}
          className={clsx(isHoliday && classes.textGray700Important)}
        />
      </div>

      <Collapse in={expanded}>
        <div style={{ padding: '0 30px 20px 30px' }}>
          <PriceSettlement2
            simulator={item.simulator}
            cargoSummary={cargoSummary}
            tradeCondtion={resolvedPostBookingInquiry!.trade_condition}
            disabled={isHoliday}
          />
        </div>
      </Collapse>
    </div>
  );
});

RoutePlanCard.displayName = 'RoutePlanCard';

export const RoutePlanList: React.FC<{
  name: string;
  routePlanSimulators: RoutePlanSimulator[];
}> = ({ name, routePlanSimulators }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { ownerCompanyCountry } = useBookingCreateContext();

  const firstCountry = ownerCompanyCountry;
  const secondaryCountry = ownerCompanyCountry === 'cn' ? 'jp' : 'cn';

  const [routePlanOrderField] = useField<RoutePlanOrder>('route_plan_order');

  const [serviceNamesField] = useField<ServiceName[]>(joinKeyPath(name, 'service_names'));

  const [receivingWarehouseCitiesField] = useField<string[]>(
    joinKeyPath(name, 'receiving_warehouse_cities'),
  );

  const [hasPickupsField, ,] = useField<'true' | 'false'>('has_pickups');
  const [moveInDateField, ,] = useField<string>('move_in_date');

  const [cargosField] = useField<Cargo2[]>('cargos2');

  const cargoSummary = useMemo(() => {
    const summaries = cargosField.value.map(getSummaryOfCargo2);
    const unit = sum(summaries.map((s) => s.unit));
    const cbm = sum(summaries.map((s) => s.cbm));
    const kg = sum(summaries.map((s) => s.kg));
    return { unit, cbm, kg };
  }, [cargosField.value]);

  const getIsHoliday = useCallback(
    (item: RoutePlanSimulator) => {
      if (hasPickupsField.value === 'true') return false;

      return receivingWarehouseChineseHolidays.some(
        (h) =>
          (h.city === item.route_plan.receiving_city ||
            item.route_plan.receiving_warehouses.every((r) => r.city === h.city)) &&
          moveInDateField.value >= h.begin &&
          moveInDateField.value <= h.end,
      );
    },
    [hasPickupsField.value, moveInDateField.value],
  );

  const displayRoutePlansSimulators = useMemo(() => {
    const filtered = routePlanSimulators.filter(
      (r) =>
        (serviceNamesField.value.length === 0 ||
          serviceNamesField.value.includes(r.route_plan.service_name)) &&
        (receivingWarehouseCitiesField.value.length === 0 ||
          receivingWarehouseCitiesField.value.includes(r.route_plan.receiving_city)),
    );

    switch (routePlanOrderField.value) {
      case 'RECOMMENDED':
        const cheapestThree: RoutePlanSimulator[] = [];
        const presorted = sortBy(filtered, (r) => getSimulatorResultTotalPrice(r.simulator));
        // Select cheapeast of each route
        for (const serviceName of serviceNamesField.value) {
          const item = minBy(
            presorted.filter((v) => v.route_plan.service_name === serviceName),
            (r) => getSimulatorResultTotalPrice(r.simulator),
          );
          if (item) {
            cheapestThree.push(item);
          }
        }

        return [
          ...sortBy(
            cheapestThree,
            (r) => getSimulatorResultTotalPrice(r.simulator, firstCountry),
            (r) => getSimulatorResultTotalPrice(r.simulator, secondaryCountry),
          ),
          ...presorted.filter((r) => !cheapestThree.includes(r)),
        ];
      case 'FAST':
        return sortBy(filtered, (r) => r.simulator.delivery_days);
      case 'CHEAP':
        return sortBy(
          filtered,
          (r) => getSimulatorResultTotalPrice(r.simulator, firstCountry),
          (r) => getSimulatorResultTotalPrice(r.simulator, secondaryCountry),
        );
    }
  }, [
    routePlanSimulators,
    serviceNamesField.value,
    receivingWarehouseCitiesField.value,
    routePlanOrderField.value,
  ]);

  const nonHolidayFirstRoutePlansSimulators = useMemo(() => {
    return sortBy(displayRoutePlansSimulators, (r) => (getIsHoliday(r) ? 1 : 0));
  }, [displayRoutePlansSimulators, getIsHoliday]);

  const routePlanCards = useMemo(() => {
    return nonHolidayFirstRoutePlansSimulators
      .filter((item) => !getIsHoliday(item))
      .map((item) => (
        <RoutePlanCard
          item={item}
          cargoSummary={cargoSummary}
          key={`${item.route_plan.plan_id}:${item.route_plan.option_id}`}
          isHoliday={getIsHoliday(item)}
        />
      ));
  }, [nonHolidayFirstRoutePlansSimulators, getIsHoliday]);

  return (
    <div className={classes.routePlanListContainer}>
      <RoutePlanOrderOptions />

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          rowGap: '1rem',
        }}
        id="route-plan-list-container"
      >
        {routePlanCards.length > 0 ? (
          routePlanCards
        ) : (
          <div
            className={clsx(
              classes.textGray500Important,
              classes.middleText,
              classes.textAlignCenter,
              classes.flexCenter,
            )}
          >
            <div className={classes.split} />
            {'　'}
            {routePlanSimulators.length === 0
              ? strings.noRoutesAvailable
              : strings.noFilteredRoutesAvailable}
            {'　'}
            <div className={classes.split} />
          </div>
        )}
      </div>
    </div>
  );
};
